import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import {
  isPushMode,
  openPush,
  closePush,
  watchPushBreakpoint,
  rememberOpen,
  forgetOpen,
  wasOpen,
  clearPreopen,
  adoptPush,
} from '@theme/push-drawer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /**
   * Abre el carrito.
   *
   * En desktop (>= 990px) usa el modo PUSH: el contenido de la pagina se encoge y
   * el carrito ocupa el hueco, sin backdrop y dejando la pagina usable. Eso obliga
   * a soltar el showModal() de DialogComponent (que impone top layer, backdrop y
   * focus-trap) y a manejar Escape a mano; de eso se encarga push-drawer.js.
   *
   * En movil no hay ancho que ceder: cae al comportamiento de siempre (modal a
   * pantalla completa con scroll bloqueado), que es el de DialogComponent.
   */
  showDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return;

    const abierto = openPush(dialog, { onEscape: () => this.closeDialog() });

    if (!abierto) {
      // Movil: comportamiento modal de siempre. Marcamos que a partir de aqui el
      // scroll lo ajustamos nosotros (body fixed al abrir, restauracion al cerrar)
      // para que el header sticky ignore ese vaiven y no aparezca/desaparezca de golpe.
      document.documentElement.setAttribute('data-scroll-adjusting', '');
      return super.showDialog();
    }

    // El drawer esta pensado para dejarlo abierto mientras navegas: si te vas a otra
    // pagina, debe seguir abierto al llegar.
    rememberOpen('cart-drawer');

    this.dispatchEvent(new DialogOpenEvent());
  };

  /** Cierra el carrito, deshaciendo el push si estaba activo. */
  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    forgetOpen();

    // `closePush` devuelve true si estabamos en modo push (desktop) y lo deshizo;
    // false en movil (modal). OJO: NO delegar el cierre modal a `super.closeDialog()`.
    // En DialogComponent `closeDialog` es un CAMPO DE INSTANCIA (arrow), no un metodo
    // del prototipo, asi que `super.closeDialog` es undefined y lanzaba
    // `TypeError: ... is not a function` — el carrito quedaba atascado abierto en movil.
    // Cerramos aqui los dos casos con el mismo patron robusto.
    const push = closePush(dialog);

    dialog.classList.add('dialog-closing');

    // El cierre espera a que acabe la animacion de salida. Si esa animacion no llega a
    // correr NUNCA — reduced-motion, la pestaña en segundo plano, o cualquier CSS que la
    // anule — `animationend` no se dispara y el carrito se queda ATASCADO abierto.
    // De ahi el plazo maximo: pase lo que pase, el drawer cierra.
    let cerrado = false;
    const cerrar = () => {
      if (cerrado) return;
      cerrado = true;

      const html = document.documentElement;
      const prevScrollBehavior = html.style.scrollBehavior;
      const top = document.body.style.top;

      // Cerramos con el body AUN bloqueado (position:fixed). Al cerrar un <dialog>
      // modal el navegador DIFIERE un scroll para revelar el elemento que recupera el
      // foco (el trigger del carrito, arriba en el header); si en ese momento el body
      // sigue fijo, ese scroll es un no-op (la ventana no puede desplazarse). Esperamos
      // a que ese scroll de foco pase y recien entonces desbloqueamos y restauramos la
      // posicion de un golpe — sin parpadeo ni animacion. En push el body nunca se
      // bloqueo, asi que se cierra directo.
      if (!push) html.style.scrollBehavior = 'auto';

      dialog.close();
      dialog.classList.remove('dialog-closing');
      this.dispatchEvent(new DialogCloseEvent());

      if (push) return;

      setTimeout(() => {
        const y = top ? -parseInt(top, 10) || 0 : 0;
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        window.scrollTo({ top: y, behavior: 'instant' });
        html.style.scrollBehavior = prevScrollBehavior;
        // Liberar al header un par de frames despues, cuando ya ignoro el scroll de
        // restauracion, para que no lo lea como un cambio de direccion.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            document.documentElement.removeAttribute('data-scroll-adjusting');
          });
        });
      }, 150);
    };

    dialog.addEventListener('animationend', cerrar, { once: true });
    setTimeout(cerrar, 500);
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // Si el usuario cruza el breakpoint con el carrito abierto, el modo push deja
    // de ser valido: se cierra para no dejar la pagina encogida sin drawer.
    watchPushBreakpoint(() => this.closeDialog());

    this.#restoreAfterNavigation();
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  /**
   * El carrito esta pensado para dejarlo abierto MIENTRAS navegas: si te vas del home a
   * una PDP, tiene que seguir abierto al llegar.
   *
   * Para cuando este modulo carga, los scripts inline del layout ya han hecho el trabajo
   * visible: la pagina nace comprimida y el <dialog> ya viene con `open`. Aqui solo se
   * recoge ese estado y se le engancha lo que necesita JS (Escape, eventos).
   *
   * El `showDialog()` del final es la red de seguridad para si los inline no corrieron.
   */
  #restoreAfterNavigation() {
    const { dialog } = this.refs;

    if (dialog?.open) {
      adoptPush(dialog, { onEscape: () => this.closeDialog() });
      this.dispatchEvent(new DialogOpenEvent());
      return;
    }

    if (isPushMode() && wasOpen('cart-drawer')) {
      dialog?.style.setProperty('--dialog-drawer-opening-animation', 'none');
      this.showDialog().then(() => {
        dialog?.style.removeProperty('--dialog-drawer-opening-animation');
      });
    }

    // Aqui no habia nada que restaurar (movil, o el carrito estaba cerrado): si el script
    // del <head> se adelanto y reservo el hueco, deshacerlo.
    clearPreopen();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
